import type {
  FirelightPrefillCatalogApplication,
  FirelightPrefillCatalogApplicationInput,
  FirelightPrefillCatalogFieldInput,
  FirelightPrefillCatalogOption,
} from '@fig/shared';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { ErrorState } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCreateFirelightPrefillApplication,
  useDeleteFirelightPrefillApplication,
  useFirelightPrefillApplication,
  useFirelightPrefillCatalog,
  useUpdateFirelightPrefillApplication,
} from '@/hooks/api/useFirelightPrefillCatalog';
import { useToast } from '@/providers/toast-provider';

const INPUT_TYPES = [
  'text',
  'date',
  'email',
  'phone',
  'select',
  'radio',
  'checkbox',
  'hidden',
] as const;
const REQUIRED_STATUSES = ['observed_required', 'not_required', 'unknown'] as const;
const REVIEW_STATUSES = ['verified', 'drafted_from_audit', 'needs_value_confirmation'] as const;

type ReviewStatus = (typeof REVIEW_STATUSES)[number];

const REVIEW_STATUS_VARIANT: Record<ReviewStatus, 'success' | 'warning' | 'info'> = {
  verified: 'success',
  drafted_from_audit: 'warning',
  needs_value_confirmation: 'info',
};

interface DraftField extends FirelightPrefillCatalogFieldInput {
  _key: string;
}

let draftFieldCounter = 0;
const nextDraftKey = () => `draft-${(draftFieldCounter += 1)}`;

/** Encode options as "value=label" lines for lightweight editing. */
function optionsToText(options: FirelightPrefillCatalogOption[] | null | undefined): string {
  if (!options || options.length === 0) return '';
  return options.map((o) => `${o.value}=${o.label}`).join('\n');
}

function textToOptions(text: string): FirelightPrefillCatalogOption[] | null {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return null;
  return lines.map((line) => {
    const eq = line.indexOf('=');
    if (eq === -1) return { value: line, label: line };
    return { value: line.slice(0, eq).trim(), label: line.slice(eq + 1).trim() };
  });
}

export function FirelightPrefillTab() {
  const { data, isLoading, error, refetch } = useFirelightPrefillCatalog();
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const deleteMutation = useDeleteFirelightPrefillApplication();
  const { showToast } = useToast();

  const handleDelete = (app: FirelightPrefillCatalogApplication) => {
    if (!window.confirm(`Delete prefill mapping for "${app.carrierName} / ${app.productName}"?`)) {
      return;
    }
    deleteMutation.mutate(app.id, {
      onSuccess: () => showToast({ title: 'Prefill mapping deleted.' }),
      onError: () => showToast({ title: 'Failed to delete mapping.' }),
    });
  };

  if (error) {
    return <ErrorState message="Failed to load the Firelight prefill catalog." onRetry={refetch} />;
  }

  // Editing/creating opens a full-page editor in place of the list.
  if (editingId) {
    return (
      <ApplicationEditorPage
        applicationId={editingId === 'new' ? null : editingId}
        onClose={() => setEditingId(null)}
      />
    );
  }

  const meta = data?.meta;
  const applications = data?.applications ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-info/20 bg-info/5 p-3 text-sm text-muted-foreground">
        Phase 1: edits here are stored in the database but are not yet used by the live Firelight
        e-application launch flow, which still reads the bundled{' '}
        <code>prefill-data-items.json</code>.
      </div>

      <Card className="bg-surface-0" style={{ boxShadow: 'var(--shadow-1)' }}>
        <CardHeader className="space-y-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>Firelight prefill catalog</CardTitle>
              <CardDescription>
                Maps internal value keys to Firelight DataItemIds per carrier/product application.
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setEditingId('new')}>
              <Plus className="mr-1 h-4 w-4" />
              Add application
            </Button>
          </div>
          {meta && (
            <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-muted-foreground">
              <Badge variant="outline">v{meta.version}</Badge>
              {meta.generatedFrom && <Badge variant="secondary">{meta.generatedFrom}</Badge>}
              <span>{meta.targetCarrierPatterns.length} target carrier patterns</span>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : applications.length === 0 ? (
            <p className="text-sm text-muted-foreground">No prefill applications yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Carrier</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Jurisdiction</TableHead>
                  <TableHead>Review status</TableHead>
                  <TableHead className="text-right">Fields</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell className="font-medium">{app.carrierName}</TableCell>
                    <TableCell>{app.productName}</TableCell>
                    <TableCell>{app.jurisdictionTested ?? '—'}</TableCell>
                    <TableCell>
                      {app.reviewStatus ? (
                        <Badge variant={REVIEW_STATUS_VARIANT[app.reviewStatus as ReviewStatus]}>
                          {app.reviewStatus.replace(/_/g, ' ')}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{app.fieldCount}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(app.id)}>
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Delete prefill mapping for ${app.carrierName} ${app.productName}`}
                        onClick={() => handleDelete(app)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface ApplicationEditorPageProps {
  applicationId: string | null;
  onClose: () => void;
}

function ApplicationEditorPage({ applicationId, onClose }: ApplicationEditorPageProps) {
  const isNew = applicationId === null;
  const { data: loaded, isLoading } = useFirelightPrefillApplication(applicationId);
  const createMutation = useCreateFirelightPrefillApplication();
  const updateMutation = useUpdateFirelightPrefillApplication();
  const { showToast } = useToast();

  const [carrierName, setCarrierName] = useState('');
  const [carrierCode, setCarrierCode] = useState('');
  const [productName, setProductName] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [jurisdictionTested, setJurisdictionTested] = useState('');
  const [sourceApplicationName, setSourceApplicationName] = useState('');
  const [reviewStatus, setReviewStatus] = useState<string>('');
  const [fields, setFields] = useState<DraftField[]>([]);
  // Preserve data we don't expose for editing so updates don't drop it.
  const [preserved, setPreserved] = useState<
    Pick<
      FirelightPrefillCatalogApplicationInput,
      'lineOfBusiness' | 'verifiedAt' | 'systemDataItemIds'
    >
  >({});

  useEffect(() => {
    if (isNew) {
      setCarrierName('');
      setCarrierCode('');
      setProductName('');
      setProductSearchTerm('');
      setJurisdictionTested('');
      setSourceApplicationName('');
      setReviewStatus('');
      setFields([]);
      setPreserved({});
      return;
    }
    if (!loaded) return;
    setCarrierName(loaded.carrierName);
    setCarrierCode(loaded.carrierCode ?? '');
    setProductName(loaded.productName);
    setProductSearchTerm(loaded.productSearchTerm ?? '');
    setJurisdictionTested(loaded.jurisdictionTested ?? '');
    setSourceApplicationName(loaded.sourceApplicationName ?? '');
    setReviewStatus(loaded.reviewStatus ?? '');
    setFields(
      (loaded.fields ?? []).map((f) => ({
        _key: nextDraftKey(),
        valueKey: f.valueKey,
        dataItemId: f.dataItemId,
        label: f.label,
        screen: f.screen,
        inputType: f.inputType,
        requiredStatus: f.requiredStatus,
        options: f.options,
        notes: f.notes,
      }))
    );
    setPreserved({
      lineOfBusiness: loaded.lineOfBusiness,
      verifiedAt: loaded.verifiedAt,
      systemDataItemIds: loaded.systemDataItemIds,
    });
  }, [isNew, loaded]);

  const updateField = (key: string, patch: Partial<DraftField>) => {
    setFields((prev) => prev.map((f) => (f._key === key ? { ...f, ...patch } : f)));
  };
  const removeField = (key: string) => setFields((prev) => prev.filter((f) => f._key !== key));
  const addField = () =>
    setFields((prev) => [
      ...prev,
      {
        _key: nextDraftKey(),
        valueKey: '',
        dataItemId: '',
        label: '',
        screen: '',
        inputType: 'text',
        requiredStatus: 'unknown',
        options: null,
        notes: '',
      },
    ]);

  const validationError = useMemo(() => {
    if (!carrierName.trim()) return 'Carrier name is required.';
    if (!productName.trim()) return 'Product name is required.';
    for (const f of fields) {
      if (!f.valueKey.trim() || !f.dataItemId.trim()) {
        return 'Every field needs a value key and a DataItemId.';
      }
    }
    return null;
  }, [carrierName, productName, fields]);

  const isSaving = createMutation.isPending || updateMutation.isPending;
  // For an existing application, block saving until its data has loaded so a
  // failed/pending fetch can't overwrite the persisted record with empty values.
  const notLoaded = !isNew && !loaded;

  const handleSave = () => {
    if (validationError || notLoaded) return;
    const input: FirelightPrefillCatalogApplicationInput = {
      carrierName: carrierName.trim(),
      carrierCode: carrierCode.trim() || null,
      productName: productName.trim(),
      productSearchTerm: productSearchTerm.trim() || null,
      lineOfBusiness: preserved.lineOfBusiness ?? 'Annuity',
      jurisdictionTested: jurisdictionTested.trim() || null,
      sourceApplicationName: sourceApplicationName.trim() || null,
      verifiedAt: preserved.verifiedAt ?? null,
      reviewStatus: (reviewStatus || null) as ReviewStatus | null,
      systemDataItemIds: preserved.systemDataItemIds ?? null,
      fields: fields.map((f) => ({
        valueKey: f.valueKey.trim(),
        dataItemId: f.dataItemId.trim(),
        label: f.label?.trim() || null,
        screen: f.screen?.trim() || null,
        inputType: f.inputType,
        requiredStatus: f.requiredStatus,
        options: f.options ?? null,
        notes: f.notes?.trim() || null,
      })),
    };

    const onSuccess = () => {
      showToast({ title: isNew ? 'Prefill mapping created.' : 'Prefill mapping saved.' });
      onClose();
    };
    const onError = () => showToast({ title: 'Failed to save mapping.' });

    if (isNew) {
      createMutation.mutate(input, { onSuccess, onError });
    } else {
      updateMutation.mutate({ id: applicationId as string, input }, { onSuccess, onError });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              {isNew ? 'New prefill application' : 'Edit prefill application'}
            </h2>
            <p className="text-sm text-muted-foreground">
              Maps internal value keys (e.g. <code>owner.firstName</code>) to Firelight DataItemIds.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || notLoaded || Boolean(validationError)}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {validationError && <p className="text-xs text-destructive">{validationError}</p>}

      {!isNew && isLoading ? (
        <p className="text-sm text-muted-foreground">Loading application…</p>
      ) : (
        <>
          <Card className="bg-surface-0" style={{ boxShadow: 'var(--shadow-1)' }}>
            <CardHeader>
              <CardTitle className="text-base">Application</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Field label="Carrier name *">
                  <Input value={carrierName} onChange={(e) => setCarrierName(e.target.value)} />
                </Field>
                <Field label="Carrier code">
                  <Input value={carrierCode} onChange={(e) => setCarrierCode(e.target.value)} />
                </Field>
                <Field label="Product name *">
                  <Input value={productName} onChange={(e) => setProductName(e.target.value)} />
                </Field>
                <Field label="Product search term">
                  <Input
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                  />
                </Field>
                <Field label="Jurisdiction tested">
                  <Input
                    value={jurisdictionTested}
                    onChange={(e) => setJurisdictionTested(e.target.value)}
                    placeholder="e.g. NC"
                  />
                </Field>
                <Field label="Source application name">
                  <Input
                    value={sourceApplicationName}
                    onChange={(e) => setSourceApplicationName(e.target.value)}
                  />
                </Field>
                <Field label="Review status">
                  <Select
                    value={reviewStatus || 'none'}
                    onValueChange={(v) => setReviewStatus(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {REVIEW_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s.replace(/_/g, ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-surface-0" style={{ boxShadow: 'var(--shadow-1)' }}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Field mappings ({fields.length})</CardTitle>
                <Button size="sm" variant="outline" onClick={addField}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add field
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {fields.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No fields. Add one to get started.
                  </p>
                )}
                {fields.map((f) => (
                  <div
                    key={f._key}
                    className="grid grid-cols-1 gap-2 rounded-md border p-2 md:grid-cols-12"
                  >
                    <Input
                      className="md:col-span-3"
                      placeholder="value key"
                      value={f.valueKey}
                      onChange={(e) => updateField(f._key, { valueKey: e.target.value })}
                    />
                    <Input
                      className="md:col-span-3"
                      placeholder="DataItemId"
                      value={f.dataItemId}
                      onChange={(e) => updateField(f._key, { dataItemId: e.target.value })}
                    />
                    <Input
                      className="md:col-span-2"
                      placeholder="screen"
                      value={f.screen ?? ''}
                      onChange={(e) => updateField(f._key, { screen: e.target.value })}
                    />
                    <div className="md:col-span-2">
                      <Select
                        value={f.inputType}
                        onValueChange={(v) =>
                          updateField(f._key, { inputType: v as DraftField['inputType'] })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {INPUT_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Select
                        value={f.requiredStatus}
                        onValueChange={(v) =>
                          updateField(f._key, { requiredStatus: v as DraftField['requiredStatus'] })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {REQUIRED_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      className="md:col-span-5"
                      placeholder="label"
                      value={f.label ?? ''}
                      onChange={(e) => updateField(f._key, { label: e.target.value })}
                    />
                    <Input
                      className="md:col-span-6"
                      placeholder="notes (optional)"
                      value={f.notes ?? ''}
                      onChange={(e) => updateField(f._key, { notes: e.target.value })}
                    />
                    <div className="flex items-center justify-end md:col-span-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Remove field"
                        onClick={() => removeField(f._key)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                    {(f.inputType === 'select' || f.inputType === 'radio') && (
                      <textarea
                        className="flex min-h-[4rem] w-full rounded-md border border-input bg-muted px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:col-span-12"
                        style={{ backgroundColor: 'hsl(var(--background))' }}
                        rows={3}
                        placeholder="options as value=label, one per line"
                        value={optionsToText(f.options)}
                        onChange={(e) =>
                          updateField(f._key, { options: textToOptions(e.target.value) })
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
